import { useEffect, useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck } from "lucide-react";
import { z } from "zod";
import {
  getActiveAuthClient,
  getActiveSession,
  subscribeToRecoveryAcrossProviders,
} from "@/integrations/supabase/authProviders";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

const schema = z
  .object({
    password: z.string().min(8, "Use at least 8 characters").max(72),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], message: "Passwords don't match" });

const BRAND = "#7C3AED";
const BRAND_DARK = "#6D28D9";

export default function ResetPassword() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // Supabase recovery deep-links land here with a hash fragment; the auth
  // client picks it up automatically and emits a PASSWORD_RECOVERY event.
  useEffect(() => {
    // Subscribe across both providers: a recovery link can land from either.
    const stop = subscribeToRecoveryAcrossProviders(() => setReady(true));
    // If the user is already signed in (force-reset flow), we're ready too.
    void getActiveSession().then((session) => {
      if (session) setReady(true);
    });
    return stop;
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({ password, confirm });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    // ⚠️ Must target the client that actually holds the session. Calling the
    // wrong one returns "Auth session missing" and the operator is stuck on the
    // forced-reset screen with no way forward.
    const client = await getActiveAuthClient();
    const { error: err } = client
      ? await client.auth.updateUser({
          password: parsed.data.password,
          data: { must_reset_password: false },
        })
      : { error: { message: "Your sign-in session has expired. Request a new reset link." } };
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    // `/` rather than `/dashboard` so the operator lands on the surface their
    // org runs. This is the whole-team path at the Supabase migration, when
    // exported passwords are unusable and everyone resets at once.
    navigate(user ? "/" : "/login", { replace: true });
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-12rem)] px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_20px_60px_-20px_rgba(76,29,149,0.18)] p-8 sm:p-10">
          <div className="text-center space-y-2 mb-8">
            <h1 className="text-[28px] font-bold tracking-tight text-slate-900">Set a new password</h1>
            <p className="text-sm text-slate-500">Choose a password you haven't used here before.</p>
          </div>

          {!ready ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  New password
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  className="h-12 rounded-xl border-slate-200 bg-slate-50/60 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30 focus-visible:border-[#7C3AED]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Confirm password
                </Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="h-12 rounded-xl border-slate-200 bg-slate-50/60 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30 focus-visible:border-[#7C3AED]"
                />
              </div>

              {error && (
                <Alert variant="destructive" className="rounded-xl">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl text-sm font-semibold text-white shadow-sm hover:shadow-lg hover:-translate-y-px transition-all border-0"
                style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})` }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          )}

          <div className="mt-7 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5 text-[#7C3AED]" />
            <span>Secured by DigaVouch identity infrastructure</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
